

import React, { useRef, useState, useContext } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { classNames } from 'primereact/utils';
import { Checkbox } from 'primereact/checkbox';
import { ColorPicker } from 'primereact/colorpicker';

import axios from '../axios';
import { useFormik } from 'formik';
import AppContext from '../Context/AppContext';
import Modal from '../Widgets/Modal';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { useMountEffect } from 'primereact/hooks';
import { InputNumber } from 'primereact/inputnumber';

const CodeAddModal = ({ hide, update }) => {
    const existingNames = useRef([])
    const { sendMessage, toast, setBlocked } = useContext(AppContext)

    const [visible, setVisible] = useState(false)
    const [contacts, setContacts] = useState([])

    useMountEffect(() => {
        fetchData();
    });

    const fetchData = async () => {
        setBlocked(true)
        try {
            const response = await axios.get('/contacts/lists');
            setContacts(response.data);
            console.log(response.data)
            setVisible(true)
        } catch (err) {
            hide()
        }
        setBlocked(false)
    };

    const formik = useFormik({
        initialValues: {
            name: "",
            description: "",
            list_id: null,
            text_message: "",
            dial_number: null,
            admin_only: false,
            logging: true,
            phone_commands: true,
            background: "000000",
            foreground: "FFFFFF",
        },
        validate: (data) => {
            let errors = {};
            if (!data.name) {
                errors.name = 'Name is required';
            }
            else if (data.name.length < 2) {
                errors.name = 'Name must have at least 2 letters'
            }
            else if (data.name.length > 15) {
                errors.name = 'Name must be not longer than 15 letters'
            }
            else if(existingNames.current.some(item => item.toLowerCase() === data.name.toLowerCase())) {
                errors.name = 'Name already exists in the database'
            }

            return errors;
        },
        onSubmit: async (data) => {
            setBlocked(true)
            try {
                const response = await axios.get('/codes/check', { params: { name: data.name } });
                if (response.data.success) {
                    existingNames.current = [...existingNames.current, ...response.data.data]
                    formik.validateForm()
                    setBlocked(false)
                    return
                }
            }
            catch (error) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to connect!', life: 3000 });
                setBlocked(false)
                return
            }

            try {
                const response = await axios.post('/codes/add', data);
                if (response.data.success) {
                    toast.current.show({ severity: 'success', summary: 'Successful', detail: `Code ${data.name} added!`, life: 3000 });
                }
                else {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to add code!', life: 3000 });
                }
            }
            catch (error) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to add code!', life: 3000 });
            }

            setBlocked(false)
            update()
            sendMessage(JSON.stringify({ type: "codes_added" }))
            setVisible(false)
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    const footer = (
        <React.Fragment>
            <Button label="Cancel" icon="pi pi-times" outlined onClick={() => { setVisible(false) }} />
            <Button label="Save" icon="pi pi-check" className="m-0" type='submit' form='submit-code' />
        </React.Fragment>
    );

    return (
        <Modal header="Add Code" visible={visible} onHide={() => setVisible(false)} transitionOptions={{ onExited: () => hide() }} footer={footer}>
            <form onSubmit={formik.handleSubmit} id='submit-code' className='formgrid grid'>
                <div className="field col-12">
                    <label htmlFor="name">Name</label>
                    <InputText name='name' id='name' value={formik.values.name} onChange={formik.handleChange} className={classNames('w-full', { 'p-invalid': isFormFieldValid('name') })} autoFocus />
                    {getFormErrorMessage('name')}
                </div>

                <div className="field col-12">
                    <label htmlFor="description">Description</label>
                    <InputTextarea rows={3} name='description' id='description' value={formik.values.description} onChange={formik.handleChange} className={classNames('w-full', { 'p-invalid': isFormFieldValid('description') })} />
                    {getFormErrorMessage('description')}
                </div>

                <div className='field col-12'>
                    <label htmlFor="list_id">Contact List</label>
                    <Dropdown placeholder='Not assigned' inputId="list_id" name="list_id" value={formik.values.list_id} onChange={formik.handleChange} options={contacts} optionLabel='name' optionValue='id' showClear className={classNames('w-full', { 'p-invalid': isFormFieldValid('list_id') })} />
                    {getFormErrorMessage('list_id')}
                </div>

                <div className="field col-12">
                    <label htmlFor="text_message">Text Message</label>
                    <InputTextarea rows={3} name='text_message' id='text_message' value={formik.values.text_message} onChange={formik.handleChange} className={classNames('w-full', { 'p-invalid': isFormFieldValid('text_message') })} />
                    {getFormErrorMessage('text_message')}
                </div>

                <div className="field col-12">
                    <label htmlFor="dial_number">Dial Number</label>
                    <InputNumber placeholder='Not assigned' name='dial_number' inputId='dial_number' useGrouping={false} value={formik.values.dial_number} onValueChange={formik.handleChange} className={classNames('w-full', { 'p-invalid': isFormFieldValid('dial_number') })} />
                    {getFormErrorMessage('dial_number')}
                </div>

                <div className="field col-12 mb-1">
                    <label>Permissions</label>
                </div>
                <div className="field col-12 flex gap-3">
                    <div className="flex align-items-center">
                        <Checkbox name="admin_only" inputId="admin_only" checked={formik.values.admin_only} onChange={formik.handleChange} />
                        <label htmlFor="admin_only" className="ml-2">Admin Only</label>
                    </div>
                    <div className="flex align-items-center">
                        <Checkbox name="logging" inputId="logging" checked={formik.values.logging} onChange={formik.handleChange} />
                        <label htmlFor="logging" className="ml-2">Logging</label>
                    </div>
                    <div className="flex align-items-center">
                        <Checkbox name="phone_commands" inputId="phone_commands" checked={formik.values.phone_commands} onChange={formik.handleChange} />
                        <label htmlFor="phone_commands" className="ml-2">Phone Commands</label>
                    </div>
                </div>

                <div className="field col-6">
                    <label htmlFor="background">Background</label>
                    <ColorPicker name='background' inputId="background" value={formik.values.background} onChange={formik.handleChange} className='block' inputClassName='h-full w-full' format="hex" />
                </div>
                <div className="field col-6">
                    <label htmlFor="foreground">Foreground</label>
                    <ColorPicker name='foreground' inputId="foreground" value={formik.values.foreground} onChange={formik.handleChange} className='block' inputClassName='h-full w-full' format="hex" />
                </div>
            </form>
        </Modal>
    );
}

export default CodeAddModal;